import { gql, useMutation, useQuery } from '@apollo/client';
import { Link, Spinner } from '@apollo/orbit';
import React, { useEffect, useState } from 'react';

import { userSettingsRouteConfig } from 'src/app/account/routes';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { HeaderTab } from '../../common/headerTab/HeaderTab';
import { ErrorMessage } from '../../errorMessage/ErrorMessage';
import { ContinuousIntegrationTab } from '../continuousIntegrationTab/ContinuousIntegrationTab';
import { EndpointTab } from '../endpointTab/EndpointTab';
import { FederationTab } from '../federationTab/FederationTab';
import { SchemaFileTab } from '../schemaFileTab/SchemaFileTab';
import { SchemaReportingTab } from '../schemaReportingTab/SchemaReportingTab';

const legacyProvisionDefaultKey = gql<
  GraphQLTypes.LegacyProvisionDefaultKey,
  GraphQLTypes.LegacyProvisionDefaultKeyVariables
>`
  mutation LegacyProvisionDefaultKey {
    me {
      ... on UserMutation {
        user {
          id
        }
        provisionKey {
          apiKey {
            id
            keyName
            token
          }
          created
        }
      }
    }
  }
`;

const PublishSchemaQuery = gql`
  query PublishSchemaQuery($graphId: ID!, $graphVariant: String!) {
    service(id: $graphId) {
      id
      schemaTag(tag: $graphVariant) {
        id
        variant {
          id
          name
        }
        schema {
          createdAt
          hash
          document
        }
      }
      apiKeys {
        id
        keyName
        token
      }
      mostRecentCompositionPublish(graphVariant: $graphVariant) {
        graphCompositionID
        errors {
          message
          code
          locations {
            line
            column
          }
        }
      }
    }
  }
`;

function isUserMutation(
  mutation: GraphQLTypes.ProvisionDefaultKey_me,
): mutation is GraphQLTypes.ProvisionDefaultKey_me_UserMutation {
  return (
    (mutation as GraphQLTypes.ProvisionDefaultKey_me_UserMutation)
      .provisionKey !== undefined
  );
}

export enum PublishModeTab {
  Endpoint = 'introspection',
  SchemaFile = 'localSchemaFile',
  SchemaReporting = 'schemaReporting',
  Federation = 'federation',
  CI = 'continuousIntegration',
}

interface Props {
  graphId: string;
  graphVariant: string;
  firstPublish?: boolean;
  /**
   * Schema was sent so we're completing the modal
   */
  onComplete?: () => void;
  /**
   * Cancel publishing the schema
   */
  onClose?: () => void;
  __test?: {
    initialTab?: PublishModeTab;
    staging?: boolean;
  };
}

export const PublishYourSchema = ({
  graphId,
  graphVariant,
  firstPublish = true,
  onClose,
  onComplete = onClose,
  __test,
}: Props) => {
  const [currentTab, setCurrentTab] = useState<PublishModeTab>(
    __test?.initialTab ?? PublishModeTab.SchemaReporting,
  );
  const [
    finalizeProvisionKey,
    {
      data: provisionKeyData,
      error: provisionKeyError,
      loading: isProvisionKeyLoading,
      called: isProvisionKeyCalled,
    },
  ] = useMutation(legacyProvisionDefaultKey, {});

  useEffect(() => {
    finalizeProvisionKey();
  }, [finalizeProvisionKey]);

  const { data, loading, error } = useQuery<
    GraphQLTypes.PublishSchemaQuery,
    GraphQLTypes.PublishSchemaQueryVariables
  >(PublishSchemaQuery, {
    pollInterval: firstPublish ? 10000 : 0,
    variables: { graphId, graphVariant },
    skip: !graphId,
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  if (firstPublish && data?.service?.schemaTag?.variant && onComplete) {
    onComplete();
  }
  const serviceApiToken =
    data?.service?.apiKeys?.[0]?.token || '[Token Unavailable]';

  if (error) {
    return <ErrorMessage className="mb-10" error={error} />;
  }
  if (loading || !isProvisionKeyCalled || isProvisionKeyLoading) {
    return (
      <div className="flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (provisionKeyError) {
    throw new Error(provisionKeyError.message);
  }

  if (!provisionKeyData?.me || !isUserMutation(provisionKeyData.me)) {
    throw new Error('Cannot provision key for non-User identity');
  }

  if (!provisionKeyData.me.provisionKey) {
    throw new Error(
      `Failed to provision key for user ${provisionKeyData.me.user.id}`,
    );
  }

  const userToken = provisionKeyData.me.provisionKey.apiKey.token;
  if (!userToken) {
    return (
      <ErrorMessage className="mb-10">
        In order to publish a schema, you will need to use a Studio API Key. It
        appears you do not currently have any API keys enabled; visit the{' '}
        <Link to={userSettingsRouteConfig.location({})}>
          personal settings page
        </Link>{' '}
        to create one now.
      </ErrorMessage>
    );
  }
  if (data?.service?.mostRecentCompositionPublish) {
    return (
      <FederationTab
        firstPublish={firstPublish}
        graphId={graphId}
        graphVariant={graphVariant}
        serviceApiToken={serviceApiToken || '[Token Unavailable]'}
        hasBuildErrors={Boolean(
          data.service.mostRecentCompositionPublish.errors,
        )}
        onClose={onClose}
      />
    );
  }

  return (
    <>
      <div
        data-testid="register-schema-instructions"
        className="-mx-10 -mt-2 border-b border-primary px-8"
      >
        <HeaderTab
          tabName="Apollo Server"
          tabID={PublishModeTab.SchemaReporting}
          isActive={() => currentTab === PublishModeTab.SchemaReporting}
          onClick={() => setCurrentTab(PublishModeTab.SchemaReporting)}
        />
        <HeaderTab
          tabName="CI/CD"
          tabID={PublishModeTab.CI}
          isActive={() => currentTab === PublishModeTab.CI}
          onClick={() => setCurrentTab(PublishModeTab.CI)}
        />
        <HeaderTab
          tabName="Local introspection"
          tabID={PublishModeTab.Endpoint}
          isActive={() => currentTab === PublishModeTab.Endpoint}
          onClick={() => setCurrentTab(PublishModeTab.Endpoint)}
        />
        <HeaderTab
          tabName="Upload schema"
          tabID={PublishModeTab.SchemaFile}
          isActive={() => currentTab === PublishModeTab.SchemaFile}
          onClick={() => setCurrentTab(PublishModeTab.SchemaFile)}
        />
        <HeaderTab
          tabName="Federation"
          tabID={PublishModeTab.Federation}
          isActive={() => currentTab === PublishModeTab.Federation}
          onClick={() => setCurrentTab(PublishModeTab.Federation)}
        />
      </div>
      <div className="mt-4">
        {currentTab === PublishModeTab.Endpoint && (
          <EndpointTab
            firstPublish={firstPublish}
            graphId={graphId}
            graphVariant={graphVariant}
            onComplete={onComplete}
            setTab={(tab) => setCurrentTab(tab)}
          />
        )}
        {currentTab === PublishModeTab.SchemaFile && (
          <SchemaFileTab
            firstPublish={firstPublish}
            graphId={graphId}
            graphVariant={graphVariant}
            document={data?.service?.schemaTag?.schema.document}
            onComplete={onComplete}
            setTab={(tab) => setCurrentTab(tab)}
          />
        )}
        {currentTab === PublishModeTab.SchemaReporting && (
          <SchemaReportingTab
            firstPublish={firstPublish}
            graphId={graphId}
            graphVariant={graphVariant}
            serviceApiToken={serviceApiToken}
            __test={__test}
            onClose={onClose}
          />
        )}
        {currentTab === PublishModeTab.Federation && (
          <FederationTab
            firstPublish={firstPublish}
            graphId={graphId}
            graphVariant={graphVariant}
            serviceApiToken={serviceApiToken}
            hasBuildErrors={Boolean(
              data?.service?.mostRecentCompositionPublish?.errors,
            )}
            onClose={onClose}
          />
        )}
        {currentTab === PublishModeTab.CI && (
          <ContinuousIntegrationTab
            firstPublish={firstPublish}
            graphId={graphId}
            graphVariant={graphVariant}
            serviceApiToken={serviceApiToken}
            onClose={onClose}
          />
        )}
      </div>
    </>
  );
};
